import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import * as marketingFormsService from '../services/marketingFormsService'
import { questionDisplayText, optionDisplayText } from '../components/Marketing/marketingFormsUi'
import {
  PROSPERITY_BRANDING_DEFAULTS,
  brandingToCssVars,
  hasBrandTemplate,
  hasCustomBrandingValues,
  mergeEffectiveBranding
} from '../utils/marketingFormBranding'

const { FiCheck } = FiIcons

const PLATFORM_NAME = 'Prosperity Leaders'

const COPY = {
  es: {
    unavailable: 'Este formulario no está disponible.',
    name: 'Nombre',
    phone: 'Teléfono',
    email: 'Correo electrónico',
    nameRequired: 'El nombre es obligatorio.',
    phoneRequired: 'El teléfono es obligatorio.',
    fieldRequired: 'Este campo es obligatorio.',
    submit: 'Enviar',
    submitting: 'Enviando…',
    submitError: 'No se pudo enviar el formulario. Inténtalo de nuevo.',
    thankYou: '¡Gracias!',
    thankYouSub: 'Tu respuesta ha sido enviada.',
    quizThankYou: 'Gracias por completar el quiz.',
    passed: '¡Aprobado!',
    notPassed: 'No aprobado',
    yes: 'Sí',
    no: 'No',
    yourAnswer: 'Tu respuesta'
  },
  en: {
    unavailable: 'This form is not available.',
    name: 'Name',
    phone: 'Phone',
    email: 'Email',
    nameRequired: 'Name is required.',
    phoneRequired: 'Phone is required.',
    fieldRequired: 'This field is required.',
    submit: 'Submit',
    submitting: 'Submitting…',
    submitError: 'Could not submit the form. Please try again.',
    thankYou: 'Thank you!',
    thankYouSub: 'Your response has been submitted.',
    quizThankYou: 'Thank you for completing the quiz.',
    passed: 'Passed!',
    notPassed: 'Not passed',
    yes: 'Yes',
    no: 'No',
    yourAnswer: 'Your answer'
  }
}

const PublicFormPage = () => {
  const { slug } = useParams()
  const [form, setForm] = useState(null)
  const [branding, setBranding] = useState(null)
  const [loadState, setLoadState] = useState('loading')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [answers, setAnswers] = useState({})
  const [errors, setErrors] = useState({})
  const [submitError, setSubmitError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitResult, setSubmitResult] = useState(null)

  const lang = form?.language === 'en' ? 'en' : 'es'
  const copy = COPY[lang]

  const questions = useMemo(
    () =>
      [...(form?.marketing_form_questions || [])].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0)
      ),
    [form]
  )

  const cssVars = useMemo(
    () => (branding ? brandingToCssVars(branding) : brandingToCssVars(PROSPERITY_BRANDING_DEFAULTS)),
    [branding]
  )

  const headline = useMemo(() => {
    if (branding?.headline) return branding.headline
    return form?.title || ''
  }, [branding, form])

  const subtitle = useMemo(() => {
    if (branding?.subtitle) return branding.subtitle
    return form?.description || ''
  }, [branding, form])

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoadState('loading')
      setForm(null)
      setBranding(null)

      const { data, error } = await marketingFormsService.getPublicForm(slug)
      if (cancelled) return

      if (error || !data) {
        setLoadState('unavailable')
        return
      }

      let template = data.brand_templates
      if (Array.isArray(template)) template = template[0] ?? null

      let effective = mergeEffectiveBranding(template, data.custom_branding)

      if (!hasBrandTemplate(template) && !hasCustomBrandingValues(data.custom_branding)) {
        const { data: defaultTpl } = await marketingFormsService.getPublicDefaultBrandTemplate()
        if (!cancelled && defaultTpl) {
          effective = mergeEffectiveBranding(defaultTpl, data.custom_branding)
        } else if (!cancelled) {
          effective = mergeEffectiveBranding(PROSPERITY_BRANDING_DEFAULTS, data.custom_branding)
        }
      }

      if (!cancelled) {
        setForm(data)
        setBranding(effective)
        setLoadState('ready')
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [slug])

  const setAnswer = useCallback((questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }))
    setErrors((prev) => {
      const next = { ...prev }
      delete next[questionId]
      return next
    })
  }, [])

  const validate = useCallback(() => {
    const next = {}
    if (!name.trim()) next.name = copy.nameRequired
    if (!phone.trim()) next.phone = copy.phoneRequired

    questions.forEach((q) => {
      if (!q.required) return
      const val = answers[q.id]
      if (val == null || val === '' || (Array.isArray(val) && val.length === 0)) {
        next[q.id] = copy.fieldRequired
      }
    })

    setErrors(next)
    return Object.keys(next).length === 0
  }, [answers, copy, name, phone, questions])

  const buildPayloadAnswers = useCallback(() => {
    return questions.map((q) => {
      const val = answers[q.id]
      if (q.type === 'multiple_choice') {
        return {
          question_id: q.id,
          answer_text: null,
          selected_option_id: val || null
        }
      }
      if (q.type === 'yes_no') {
        const opts = [...(q.marketing_form_options || [])].sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0)
        )
        if (opts.length >= 2) {
          return {
            question_id: q.id,
            answer_text: null,
            selected_option_id: val || null
          }
        }
        return {
          question_id: q.id,
          answer_text: val || null,
          selected_option_id: null
        }
      }
      return {
        question_id: q.id,
        answer_text: val != null ? String(val) : null,
        selected_option_id: null
      }
    })
  }, [answers, questions])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitError('')
    if (!validate()) return

    setSubmitting(true)
    const { data, error } = await marketingFormsService.submitFormResponse(
      form.id,
      email.trim() || null,
      name.trim(),
      phone.trim(),
      buildPayloadAnswers()
    )
    setSubmitting(false)

    if (error) {
      setSubmitError(error.message || copy.submitError)
      return
    }
    setSubmitResult(data)
    setSubmitted(true)
  }

  if (loadState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (loadState === 'unavailable') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="text-center max-w-md">
          <p className="text-lg text-slate-700 font-medium">{COPY.es.unavailable}</p>
          <p className="text-sm text-slate-500 mt-2">{COPY.en.unavailable}</p>
        </div>
      </div>
    )
  }

  const showQuizResults =
    form?.type === 'quiz' && form?.show_results_to_respondent && submitResult

  return (
    <div
      className="min-h-screen public-form-page"
      style={{
        ...cssVars,
        backgroundColor: 'var(--brand-bg)',
        color: 'var(--brand-text)',
        fontFamily: 'var(--brand-font)'
      }}
    >
      {branding?.custom_css ? (
        <style>{`.public-form-page { ${branding.custom_css} }`}</style>
      ) : null}

      <header className="px-4 sm:px-6 py-8 sm:py-10">
        <div className="max-w-[680px] mx-auto text-center">
          {branding?.logo_url ? (
            <img
              src={branding.logo_url}
              alt=""
              className="h-12 sm:h-14 mx-auto mb-6 object-contain"
            />
          ) : (
            <p
              className="text-lg sm:text-xl font-semibold mb-6 tracking-tight"
              style={{ color: 'var(--brand-primary)' }}
            >
              {PLATFORM_NAME}
            </p>
          )}
          <h1 className="text-2xl sm:text-3xl font-bold leading-tight">{headline}</h1>
          {subtitle ? (
            <p className="mt-3 text-base sm:text-lg opacity-90 max-w-xl mx-auto">{subtitle}</p>
          ) : null}
        </div>
      </header>

      <main className="px-4 sm:px-6 pb-16">
        <div className="max-w-[680px] mx-auto">
          {submitted ? (
            showQuizResults ? (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-lg border border-black/5 p-8 sm:p-12 text-center"
              >
                <p
                  className="text-5xl sm:text-6xl font-bold tabular-nums"
                  style={{ color: 'var(--brand-primary)' }}
                >
                  {submitResult.score ?? 0} / {submitResult.total_questions ?? 0}
                </p>
                {submitResult.passed != null ? (
                  <span
                    className={`inline-block mt-4 px-4 py-1.5 rounded-full text-sm font-semibold ${
                      submitResult.passed
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {submitResult.passed ? copy.passed : copy.notPassed}
                  </span>
                ) : null}
                <p className="mt-6 text-slate-600">{copy.quizThankYou}</p>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-lg border border-black/5 p-8 sm:p-12 text-center"
              >
                <div
                  className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-6"
                  style={{ backgroundColor: 'var(--brand-primary)', color: '#fff' }}
                >
                  <SafeIcon icon={FiCheck} className="w-8 h-8" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">{copy.thankYou}</h2>
                <p className="mt-2 text-slate-600">{copy.thankYouSub}</p>
              </motion.div>
            )
          ) : (
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl shadow-lg border border-black/5 p-6 sm:p-8 space-y-6"
            >
              <div className="space-y-4 pb-2 border-b border-slate-100">
                <div>
                  <label className="block text-sm font-medium text-slate-800 mb-1">
                    {copy.name}
                    <span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value)
                      setErrors((prev) => {
                        const n = { ...prev }
                        delete n.name
                        return n
                      })
                    }}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                    autoComplete="name"
                  />
                  {errors.name ? (
                    <p className="text-xs text-red-600 mt-1">{errors.name}</p>
                  ) : null}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-800 mb-1">
                    {copy.phone}
                    <span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value)
                      setErrors((prev) => {
                        const n = { ...prev }
                        delete n.phone
                        return n
                      })
                    }}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                    autoComplete="tel"
                  />
                  {errors.phone ? (
                    <p className="text-xs text-red-600 mt-1">{errors.phone}</p>
                  ) : null}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-800 mb-1">
                    {copy.email}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                    autoComplete="email"
                  />
                </div>
              </div>

              {questions.map((q, idx) => (
                <FormQuestionField
                  key={q.id}
                  question={q}
                  index={idx + 1}
                  language={lang}
                  copy={copy}
                  value={answers[q.id]}
                  onChange={(v) => setAnswer(q.id, v)}
                  error={errors[q.id]}
                />
              ))}

              {submitError ? (
                <p className="text-sm text-red-600 text-center">{submitError}</p>
              ) : null}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-opacity hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: 'var(--brand-primary)' }}
              >
                {submitting ? copy.submitting : copy.submit}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  )
}

const FormQuestionField = ({ question, index, language, copy, value, onChange, error }) => {
  const label = questionDisplayText(question, language)
  const options = [...(question.marketing_form_options || [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  )

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-800">
        {index}. {label}
        {question.required ? <span className="text-red-500 ml-0.5">*</span> : null}
      </label>

      {question.type === 'short_text' && (
        <textarea
          rows={3}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={copy.yourAnswer}
          className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
        />
      )}

      {question.type === 'rating' && (
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
            const selected = Number(value) === n
            return (
              <button
                key={n}
                type="button"
                onClick={() => onChange(String(n))}
                className={`w-10 h-10 rounded-lg border text-sm font-medium transition-colors ${
                  selected
                    ? 'text-white border-transparent'
                    : 'border-slate-200 text-slate-600 hover:border-slate-300 bg-white'
                }`}
                style={
                  selected
                    ? { backgroundColor: 'var(--brand-primary)', borderColor: 'var(--brand-primary)' }
                    : undefined
                }
              >
                {n}
              </button>
            )
          })}
        </div>
      )}

      {question.type === 'multiple_choice' && (
        <div className="space-y-2">
          {options.map((opt) => (
            <label
              key={opt.id}
              className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer"
            >
              <input
                type="radio"
                name={`q-${question.id}`}
                checked={value === opt.id}
                onChange={() => onChange(opt.id)}
                className="text-[var(--brand-primary)] focus:ring-[var(--brand-primary)]"
              />
              {optionDisplayText(opt, language)}
            </label>
          ))}
          {options.length === 0 ? (
            <p className="text-xs text-slate-400">No options configured</p>
          ) : null}
        </div>
      )}

      {question.type === 'yes_no' && (
        <YesNoField
          question={question}
          options={options}
          language={language}
          copy={copy}
          value={value}
          onChange={onChange}
        />
      )}

      {error ? <p className="text-xs text-red-600">{error}</p> : null}
    </div>
  )
}

const YesNoField = ({ question, options, language, copy, value, onChange }) => {
  if (options.length >= 2) {
    return (
      <div className="space-y-2">
        {options.map((opt) => (
          <label
            key={opt.id}
            className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer"
          >
            <input
              type="radio"
              name={`q-${question.id}`}
              checked={value === opt.id}
              onChange={() => onChange(opt.id)}
              className="text-[var(--brand-primary)] focus:ring-[var(--brand-primary)]"
            />
            {optionDisplayText(opt, language)}
          </label>
        ))}
      </div>
    )
  }

  const yesVal = language === 'en' ? 'Yes' : 'Sí'
  const noVal = language === 'en' ? 'No' : 'No'

  return (
    <div className="flex flex-wrap gap-4">
      {[yesVal, noVal].map((optVal) => (
        <label
          key={optVal}
          className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer"
        >
          <input
            type="radio"
            name={`q-${question.id}`}
            checked={value === optVal}
            onChange={() => onChange(optVal)}
            className="text-[var(--brand-primary)] focus:ring-[var(--brand-primary)]"
          />
          {optVal === yesVal ? copy.yes : copy.no}
        </label>
      ))}
    </div>
  )
}

export default PublicFormPage
